import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Save, Search, Globe, ChevronDown, ChevronUp, LogOut, RefreshCw, Check, FileText } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const languages = [
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

const pageLabels = {
  global: { name: 'Globaal', icon: '🌐', description: 'Navigatie, footer en bedrijfsgegevens' },
  home: { name: 'Homepage', icon: '🏠', description: 'Hero sectie, diensten preview en CTA' },
  services: { name: 'Diensten', icon: '🛠️', description: 'Alle 8 diensten met beschrijvingen' },
  portfolio: { name: 'Portfolio', icon: '📁', description: 'Projecten en portfolio items' },
  pricing: { name: 'Prijzen', icon: '💰', description: 'Pakketprijzen en extra opties' },
};

const sectionLabels = {
  company: 'Bedrijfsgegevens',
  nav: 'Navigatie',
  footer: 'Footer',
  hero: 'Hero Sectie',
  services: 'Diensten Preview',
  cta: 'Call-to-Action',
  packages: 'Pakketten',
  service_1: 'Dienst 1: Website op maat',
  service_2: 'Dienst 2: Webshop ontwikkeling',
  service_3: 'Dienst 3: Professioneel ontwerp',
  service_4: 'Dienst 4: SEO optimalisatie',
  service_5: 'Dienst 5: Mobile-first design',
  service_6: 'Dienst 6: Support & onderhoud',
  service_7: 'Dienst 7: Technisch beheer',
  service_8: 'Dienst 8: Meertalige websites',
  project_1: 'Project 1: Van der Berg Consultancy',
  project_2: 'Project 2: Bloemist Sophie',
  project_3: 'Project 3: Yoga Studio Zen',
  project_4: 'Project 4: Jansen & Partners',
  project_5: 'Project 5: Tech Startup XYZ',
  project_6: 'Project 6: Mode Boutique Emma',
  starter: 'Pakket: Starter',
  professional: 'Pakket: Professional',
  business: 'Pakket: Business',
  extras: 'Extra Opties',
};

const ContentAdminPage = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedPages, setExpandedPages] = useState({ global: true, home: true });
  const [expandedSections, setExpandedSections] = useState({});
  const [modifiedItems, setModifiedItems] = useState({});
  const [activeLanguage, setActiveLanguage] = useState('nl');
  
  const { getAuthHeaders, logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/content`);
      
      if (response.data.length === 0) {
        // Seed default content if none exists
        await axios.post(`${BACKEND_URL}/api/content/seed`, {}, {
          headers: getAuthHeaders()
        });
        const seedResponse = await axios.get(`${BACKEND_URL}/api/content`);
        setContent(seedResponse.data);
      } else {
        setContent(response.data);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/admin/login');
      } else {
        toast.error('Kon content niet laden');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (itemId, langCode, newValue) => {
    // Update local state
    setContent(prev => prev.map(item => {
      if (item.id === itemId) {
        return { ...item, [`content_${langCode}`]: newValue };
      }
      return item;
    }));
    
    // Track modified items
    setModifiedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        id: itemId,
        [`content_${langCode}`]: newValue
      }
    }));
  };

  const handleSaveAll = async () => {
    const modifiedList = Object.values(modifiedItems);
    if (modifiedList.length === 0) {
      toast.info('Geen wijzigingen om op te slaan');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/content/bulk/update`, 
        { items: modifiedList },
        { headers: getAuthHeaders() }
      );
      
      toast.success(`${modifiedList.length} item(s) opgeslagen`);
      setModifiedItems({});
    } catch (error) {
      console.error('Error saving content:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/admin/login');
      } else {
        toast.error('Kon wijzigingen niet opslaan');
      }
    } finally {
      setSaving(false);
    }
  };

  const togglePage = (page) => {
    setExpandedPages(prev => ({ ...prev, [page]: !prev[page] }));
  };

  const toggleSection = (pageSection) => {
    setExpandedSections(prev => ({ ...prev, [pageSection]: !prev[pageSection] }));
  };

  // Group content by page and section
  const groupedContent = content.reduce((acc, item) => {
    if (!acc[item.page]) {
      acc[item.page] = {};
    }
    if (!acc[item.page][item.section]) {
      acc[item.page][item.section] = [];
    }
    acc[item.page][item.section].push(item);
    return acc;
  }, {});

  // Filter content based on search
  const filteredContent = searchTerm
    ? content.filter(item => 
        item.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.content_nl?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.content_en?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : null;

  const modifiedCount = Object.keys(modifiedItems).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fc] pt-32 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-[#f8f9fc] pt-32 pb-20" data-testid="content-admin-page">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0f172a]">Content Beheer</h1>
            <p className="text-[#475569] mt-1">Welkom, {user} • Bewerk alle teksten op de website</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={fetchContent}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-300 transition-colors flex items-center gap-2"
              data-testid="refresh-content-btn"
            >
              <RefreshCw size={18} />
              Vernieuwen
            </button>
            <button
              onClick={handleSaveAll}
              disabled={saving || modifiedCount === 0}
              className={`btn-accent flex items-center gap-2 ${modifiedCount === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="save-all-btn"
            >
              {saving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save size={20} />
              )}
              Opslaan {modifiedCount > 0 && `(${modifiedCount})`}
            </button>
            <button
              onClick={() => { logout(); navigate('/'); }}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-300 transition-colors flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              Uitloggen
            </button>
          </div>
        </div>

        {/* Search and Language Filter */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Zoeken in content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20"
                data-testid="search-content"
              />
            </div>
            
            {/* Language Selector */}
            <div className="flex gap-2">
              {languages.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => setActiveLanguage(lang.code)}
                  className={`px-4 py-2 rounded-xl font-medium transition-colors flex items-center gap-2 ${
                    activeLanguage === lang.code
                      ? 'bg-[#2563EB] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`lang-btn-${lang.code}`}
                >
                  <span>{lang.flag}</span>
                  <span className="hidden sm:inline">{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Search Results */}
        {filteredContent && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-[#0f172a] mb-4">
              Zoekresultaten ({filteredContent.length})
            </h2>
            <div className="space-y-4">
              {filteredContent.map(item => (
                <ContentItemEditor
                  key={item.id}
                  item={item}
                  activeLanguage={activeLanguage}
                  isModified={!!modifiedItems[item.id]}
                  onChange={handleContentChange}
                />
              ))}
            </div>
          </div>
        )}

        {/* Grouped Content */}
        {!searchTerm && (
          <div className="space-y-4">
            {Object.entries(groupedContent).map(([page, sections]) => {
              const pageInfo = pageLabels[page] || { name: page, icon: '📄', description: '' };
              const isExpanded = expandedPages[page];
              
              return (
                <div key={page} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  {/* Page Header */}
                  <button
                    onClick={() => togglePage(page)}
                    className="w-full px-6 py-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                    data-testid={`page-toggle-${page}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{pageInfo.icon}</span>
                      <div className="text-left">
                        <h2 className="font-bold text-[#0f172a]">{pageInfo.name}</h2>
                        <p className="text-sm text-[#475569]">{pageInfo.description}</p>
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>

                  {/* Page Content */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        exit={{ height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="p-6 space-y-4">
                          {Object.entries(sections).map(([section, items]) => {
                            const sectionKey = `${page}-${section}`;
                            const isSectionExpanded = expandedSections[sectionKey] !== false;
                            
                            return (
                              <div key={section} className="border border-slate-200 rounded-xl overflow-hidden">
                                {/* Section Header */}
                                <button
                                  onClick={() => toggleSection(sectionKey)}
                                  className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                                  data-testid={`section-toggle-${sectionKey}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <FileText size={18} className="text-[#2563EB]" />
                                    <span className="font-medium text-[#0f172a]">
                                      {sectionLabels[section] || section}
                                    </span>
                                    <span className="text-sm text-[#94a3b8]">
                                      ({items.length} items)
                                    </span>
                                  </div>
                                  {isSectionExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </button>

                                {/* Section Items */}
                                <AnimatePresence>
                                  {isSectionExpanded && (
                                    <motion.div
                                      initial={{ height: 0 }}
                                      animate={{ height: 'auto' }}
                                      exit={{ height: 0 }}
                                      className="overflow-hidden"
                                    >
                                      <div className="p-4 space-y-4">
                                        {items.map(item => (
                                          <ContentItemEditor
                                            key={item.id}
                                            item={item}
                                            activeLanguage={activeLanguage}
                                            isModified={!!modifiedItems[item.id]}
                                            onChange={handleContentChange}
                                          />
                                        ))}
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {content.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[#0f172a] mb-2">Nog geen content</h3>
            <p className="text-[#475569] mb-6">
              De standaard content wordt automatisch geladen.
            </p>
          </div>
        )}
      </div>
    </main>
  );
};

// Content Item Editor Component
const ContentItemEditor = ({ item, activeLanguage, isModified, onChange }) => {
  const langField = `content_${activeLanguage}`;
  const value = item[langField] || '';
  const isTextarea = item.content_type === 'textarea' || value.length > 80;

  return (
    <div className={`relative p-4 rounded-xl border transition-colors ${
      isModified ? 'border-[#2563EB] bg-[#2563EB]/5' : 'border-slate-200 bg-white'
    }`}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <label className="text-sm font-medium text-[#0f172a] flex items-center gap-2">
          <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">
            {item.key}
          </span>
          {isModified && (
            <span className="text-xs text-[#2563EB] flex items-center gap-1">
              <Check size={12} />
              Gewijzigd
            </span>
          )}
        </label>
      </div>
      
      {isTextarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(item.id, activeLanguage, e.target.value)}
          rows={3}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 resize-none text-sm"
          placeholder={`Voer ${item.key} in...`}
          data-testid={`content-input-${item.id}`}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(item.id, activeLanguage, e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 text-sm"
          placeholder={`Voer ${item.key} in...`}
          data-testid={`content-input-${item.id}`}
        />
      )}
      
      {/* Show other language values as hints */}
      <div className="mt-2 flex flex-wrap gap-2">
        {languages.filter(l => l.code !== activeLanguage).map(lang => {
          const langValue = item[`content_${lang.code}`];
          if (!langValue) return null;
          return (
            <span key={lang.code} className="text-xs text-slate-400 truncate max-w-[200px]">
              {lang.flag} {langValue.substring(0, 30)}{langValue.length > 30 ? '...' : ''}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default ContentAdminPage;
